import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Truck, RefreshCw, CheckCircle2, Clock, PackageCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Select } from '@/components/ui/Field';
import { StatCard } from '@/components/ui/StatCard';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

interface CourierRow {
  id: string;
  courier_key: string;
  display_name: string;
}

interface ShipmentRow {
  id: string;
  courier: string | null;
  courier_partner_id: string | null;
  tracking_no: string | null;
  cod_status: string;
  courier_status: string | null;
  status_checked_at: string | null;
  dispatched_at: string | null;
  delivered_at: string | null;
  core_orders: { order_no: string; id: string } | null;
}

const COD_TONE: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  collected: 'success',
  remitted: 'success',
  failed: 'error',
  pending: 'warning',
};

const PAGE_SIZE = 20;

export default function AdminCouriers() {
  const { push } = useToast();
  const [couriers, setCouriers] = useState<CourierRow[]>([]);
  const [shipments, setShipments] = useState<ShipmentRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [deliveredCount, setDeliveredCount] = useState(0);
  const [inTransitCount, setInTransitCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [bulkChecking, setBulkChecking] = useState(false);
  const [filters, setFilters] = useState({ courierId: '', deliveredFrom: '', deliveredTo: '' });

  const load = useCallback(async () => {
    setLoading(true);
    if (couriers.length === 0) {
      const { data } = await supabase.from('core_courier_partners').select('id, courier_key, display_name').order('display_order', { ascending: true });
      setCouriers((data as CourierRow[]) ?? []);
    }

    let query = supabase
      .from('core_shipments')
      .select('id, courier, courier_partner_id, tracking_no, cod_status, courier_status, status_checked_at, dispatched_at, delivered_at, core_orders(id, order_no)', { count: 'exact' })
      .order('dispatched_at', { ascending: false })
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);
    if (filters.courierId) query = query.eq('courier_partner_id', filters.courierId);
    if (filters.deliveredFrom) query = query.gte('delivered_at', filters.deliveredFrom);
    if (filters.deliveredTo) query = query.lt('delivered_at', `${filters.deliveredTo}T23:59:59.999`);

    const { data, count } = await query;
    setShipments((data as unknown as ShipmentRow[]) ?? []);
    setTotalCount(count ?? 0);

    // Lightweight KPI counts (independent of pagination, respects the same filters)
    let deliveredQuery = supabase.from('core_shipments').select('id', { count: 'exact', head: true }).not('delivered_at', 'is', null);
    let transitQuery = supabase.from('core_shipments').select('id', { count: 'exact', head: true }).is('delivered_at', null);
    if (filters.courierId) {
      deliveredQuery = deliveredQuery.eq('courier_partner_id', filters.courierId);
      transitQuery = transitQuery.eq('courier_partner_id', filters.courierId);
    }
    if (filters.deliveredFrom) deliveredQuery = deliveredQuery.gte('delivered_at', filters.deliveredFrom);
    if (filters.deliveredTo) deliveredQuery = deliveredQuery.lt('delivered_at', `${filters.deliveredTo}T23:59:59.999`);
    const [deliveredRes, transitRes] = await Promise.all([deliveredQuery, transitQuery]);
    setDeliveredCount(deliveredRes.count ?? 0);
    setInTransitCount(transitRes.count ?? 0);

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- couriers is a load-once cache, intentionally excluded
  }, [page, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const updateFilter = (patch: Partial<typeof filters>) => {
    setPage(0);
    setFilters((f) => ({ ...f, ...patch }));
  };

  const checkStatus = async (shipmentId: string) => {
    setCheckingId(shipmentId);
    try {
      const { data, error } = await supabase.functions.invoke('core-check-shipment-status', { body: { shipment_id: shipmentId } });
      if (error) throw error;
      if (data?.ok) {
        push('success', `Latest status: ${data.courier_status}`);
      } else {
        push('error', data?.details ?? data?.error ?? 'Failed to check status');
      }
      load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to check status');
    } finally {
      setCheckingId(null);
    }
  };

  const checkAll = async () => {
    const targets = shipments.filter((s) => s.tracking_no && !s.delivered_at);
    if (targets.length === 0) {
      push('error', 'No in-transit shipments with tracking numbers on this page');
      return;
    }
    setBulkChecking(true);
    let okCount = 0;
    for (const s of targets) {
      const { data, error } = await supabase.functions.invoke('core-check-shipment-status', { body: { shipment_id: s.id } });
      if (!error && data?.ok) okCount++;
    }
    setBulkChecking(false);
    push('success', `Refreshed ${okCount}/${targets.length} shipments`);
    load();
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <h1 className="text-[22px] font-bold text-ink">Couriers</h1>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard label="Total Shipments" value={totalCount} icon={<Truck className="w-4 h-4" />} accent="primary" />
          <StatCard label="Delivered" value={deliveredCount} icon={<CheckCircle2 className="w-4 h-4" />} accent="success" />
          <StatCard label="In Transit" value={inTransitCount} icon={<Clock className="w-4 h-4" />} accent="warning" />
        </div>

        <Card>
          <CardBody className="flex flex-wrap items-end gap-3 py-3">
            <Field label="Courier">
              <Select value={filters.courierId} onChange={(e) => updateFilter({ courierId: e.target.value })} className="!w-44">
                <option value="">All couriers</option>
                {couriers.map((c) => (
                  <option key={c.id} value={c.id}>{c.display_name}</option>
                ))}
              </Select>
            </Field>
            <Field label="Delivered from">
              <Input type="date" value={filters.deliveredFrom} onChange={(e) => updateFilter({ deliveredFrom: e.target.value })} className="!w-40" />
            </Field>
            <Field label="Delivered to">
              <Input type="date" value={filters.deliveredTo} onChange={(e) => updateFilter({ deliveredTo: e.target.value })} className="!w-40" />
            </Field>
            {(filters.courierId || filters.deliveredFrom || filters.deliveredTo) && (
              <Button variant="secondary" size="sm" onClick={() => updateFilter({ courierId: '', deliveredFrom: '', deliveredTo: '' })}>Clear</Button>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Shipments"
            subtitle={totalCount > 0 ? `${page * PAGE_SIZE + 1}–${Math.min(totalCount, (page + 1) * PAGE_SIZE)} of ${totalCount}` : undefined}
            icon={<PackageCheck className="w-4 h-4" />}
            action={
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="secondary" loading={bulkChecking} onClick={checkAll}>
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh All
                </Button>
                {totalPages > 1 && (
                  <>
                    <Button size="sm" variant="secondary" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </Button>
                    <span className="text-[11.5px] text-ink-faint px-1">{page + 1} / {totalPages}</span>
                    <Button size="sm" variant="secondary" disabled={page >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
              </div>
            }
          />
          <CardBody className="space-y-1">
            {loading ? (
              <Spinner />
            ) : shipments.length === 0 ? (
              <EmptyState title="No shipments match these filters" />
            ) : (
              shipments.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-3 px-2 py-2.5 rounded-lg hover:bg-surface-muted">
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold text-ink truncate">
                      {s.core_orders ? (
                        <Link to={`/admin/orders/${s.core_orders.id}`} className="hover:underline">
                          {s.core_orders.order_no}
                        </Link>
                      ) : (
                        '—'
                      )}
                      <span className="text-ink-faint font-normal"> · {couriers.find((c) => c.id === s.courier_partner_id)?.display_name ?? s.courier ?? '—'}</span>
                    </p>
                    <p className="text-[11px] text-ink-faint">
                      Tracking {s.tracking_no ?? '—'}
                      {s.dispatched_at && ` · Shipped ${new Date(s.dispatched_at).toLocaleDateString()}`}
                      {s.delivered_at && ` · Delivered ${new Date(s.delivered_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {s.courier_status && <Badge tone="info">{s.courier_status}</Badge>}
                    <Badge tone={COD_TONE[s.cod_status] ?? 'neutral'}>COD: {s.cod_status}</Badge>
                    <Button size="sm" variant="secondary" onClick={() => checkStatus(s.id)} loading={checkingId === s.id} disabled={!s.tracking_no}>
                      <RefreshCw className="w-3.5 h-3.5" /> Check Status
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardBody>
        </Card>
      </div>
    </AppShell>
  );
}
